/* eslint-disable @typescript-eslint/no-explicit-any */
import { graphqlFetch } from '@/utils/graphqlFetch';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  CREATE_CAMPAIGN,
  GET_CAMPAIGN,
  GET_CAMPAIGNS,
  GET_CAMPAIGNS_STATS,
  GET_CAMPAIGN_DAILY_STATS,
  GET_CAMPAIGN_STATS,
} from '../../queries/b2bFinance/campaign';
import {
  CampaignOutput,
  CampaignsQuery,
  CampaignsInputUpdate,
  CampaignStatDailyOutput,
  CampaignStatOutput,
  CampaignsInput,
  CampaignsStatsOutput,
} from '../../gql/graphql';

import { UPDATE_CAMPAIGN } from '@/api/mutations/campaign';

export const useGetCampaign = (query: CampaignsQuery) => {
  return useQuery<{ data: CampaignOutput[]; total: number }, Error>({
    queryKey: ['campaigns', query],
    queryFn: async () => {
      const data = await graphqlFetch<
        { getCampaigns: any },
        { query: CampaignsQuery }
      >(GET_CAMPAIGNS, { query });
      return {
        data: data.getCampaigns.data,
        total: data.getCampaigns.total,
      };
    },
  });
};

export const useGetCampaignDetail = (id: string) => {
  return useQuery<CampaignOutput, Error>({
    queryKey: ['campaign', id],
    queryFn: async () => {
      const data = await graphqlFetch<
        { getCampaign: CampaignOutput },
        { id: string }
      >(GET_CAMPAIGN, { id });
      return data.getCampaign;
    },
    enabled: !!id,
  });
};

export const useGetCampaignDailyStats = (id: string) => {
  return useQuery({
    queryKey: ['campaignDailyStats', id],
    queryFn: async () => {
      const data = await graphqlFetch<
        { getCampaignDailyStats: CampaignStatDailyOutput },
        { id: string }
      >(GET_CAMPAIGN_DAILY_STATS, { id });
      return data.getCampaignDailyStats.data;
    },
    enabled: !!id,
  });
};

export const useGetCampaignStats = (id: string) => {
  return useQuery({
    queryKey: ['campaignStats', id],
    queryFn: async () => {
      const data = await graphqlFetch<
        { getCampaignStat: CampaignStatOutput },
        { id: string }
      >(GET_CAMPAIGN_STATS, { id });

      if (!data.getCampaignStat) {
        throw new Error('Aucune statistique trouvée pour cette campagne.');
      }

      return data.getCampaignStat;
    },
    enabled: !!id,
  });
};

export const useGetCampaignsStats = () => {
  return useQuery<CampaignsStatsOutput, Error>({
    queryKey: ['campaignsStats'],
    queryFn: async () => {
      const data = await graphqlFetch<
        { getCampaignsStats: CampaignsStatsOutput },
        null
      >(GET_CAMPAIGNS_STATS);
      return data.getCampaignsStats;
    },
  });
};

export const useCreateCampaign = () => {
  const queryClient = useQueryClient();
  return useMutation<CampaignOutput, Error, CampaignsInput>({
    mutationFn: async (input) => {
      const data = await graphqlFetch<
        { createCampaign: CampaignOutput },
        { input: CampaignsInput }
      >(CREATE_CAMPAIGN, { input });
      return data.createCampaign;
    },
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: ['campaigns'],
      }),
  });
};

export const useUpdateCampaignStatus = () => {
  const queryClient = useQueryClient();

  return useMutation<
    CampaignsInputUpdate,
    Error,
    { id: string; input: CampaignsInputUpdate }
  >({
    mutationFn: async ({ id, input }) => {
      const data = await graphqlFetch<
        { updateCampaign: CampaignsInputUpdate },
        { id: string; input: CampaignsInputUpdate }
      >(UPDATE_CAMPAIGN, { id, input });
      return data.updateCampaign;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['campaigns'] });
      queryClient.invalidateQueries({ queryKey: ['campaign', variables.id] });
      queryClient.invalidateQueries({ queryKey: ['campaignsStats'] });
    },
  });
};
